import { useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../components/api';

const useInscriptionSubmit = (
  formData,
  documents,
  tarifsSup,
  optionsPaiement,
  tranchesPaiement,
  tarifs,
  fraisInscription,
  montantVerse,
  paiementFraisInscription,
  tranchesRestantes,
  validateStep,
  setLoading,
  setError,
  navigate
) => {
  
  // Génération du libellé de versement
  const getLibelleVersement = useCallback((tranche, montant) => {
    const formatter = new Intl.NumberFormat('fr-FR');
    return `Versement de ${formatter.format(montant)} FCFA - ${tranche.pourcentage}% des frais de scolarité (${tranche.libelle})`;
  }, []);

  // Génération des lignes de versement pour l'API
  const genererLignesVersement = useCallback(() => {
    const lignesVersement = [];
    let montantRestant = Number(montantVerse) || 0;

    // Ajouter d'abord les frais d'inscription s'ils sont payés
    if (paiementFraisInscription) {
      lignesVersement.push({
        libelle: 'Paiement des frais d\'inscription',
        montant: fraisInscription,
        type_ligne: 'INSCRIPTION'
      });
    }

    // Préparation des lignes de versement pour les tranches
    for (const tranche of tranchesPaiement) {
      const montantTranche = (tarifs?.prix * tranche.pourcentage) / 100;
      const montantPaye = Math.min(montantRestant, montantTranche);
      
      if (montantPaye > 0) {
        lignesVersement.push({
          libelle: getLibelleVersement(tranche, montantPaye),
          montant: montantPaye,
          type_ligne: 'SCOLARITE',
          modalite_id: tranche.id
        });
        montantRestant -= montantPaye;
      }
    }

    // Ajouter les lignes pour les options payées
    tarifsSup.forEach(tarif => {
      if (optionsPaiement[tarif.id]) {
        lignesVersement.push({
          libelle: `Paiement de l'option: ${tarif.libelle}`,
          montant: tarif.prix,
          type_ligne: 'OPTION',
          option_id: tarif.id
        });
      }
    });

    return lignesVersement;
  }, [
    montantVerse,
    paiementFraisInscription,
    fraisInscription,
    tranchesPaiement,
    tarifs?.prix,
    getLibelleVersement,
    tarifsSup,
    optionsPaiement
  ]);

  // Génération des modalités de paiement pour l'API
  const genererModalitesPaiement = useCallback(() => {
    return tranchesPaiement.map(tranche => {
      const montantTranche = (tarifs?.prix * tranche.pourcentage) / 100;
      const montantRestant = tranchesRestantes[tranche.id] || montantTranche;
      return {
        modalite_id: tranche.id,
        montant_restant: montantRestant,
        date_echeance: tranche.date
      };
    });
  }, [tranchesPaiement, tarifs?.prix, tranchesRestantes]);

  // Fonction principale de soumission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!validateStep(3)) {
      setLoading(false);
      toast.error("Veuillez compléter tous les champs obligatoires");
      return;
    }

    try {
      console.log('🔄 Soumission de l\'inscription...');
      
      const lignesVersement = genererLignesVersement();
      const modalitesPaiement = genererModalitesPaiement();

      const inscriptionData = {
        ...formData,
        classe: parseInt(formData.classe),
        options_complementaires: tarifsSup.map(t => t.id),
        lignes_versement: lignesVersement,
        modalites_paiement: modalitesPaiement
      };

      const formDataToSend = new FormData();
      
      if (documents.fichier1) {
        formDataToSend.append('fichier1', documents.fichier1);
      }
      if (documents.fichier2) {
        formDataToSend.append('fichier2', documents.fichier2);
      }
      
      formDataToSend.append('data', JSON.stringify(inscriptionData));

      const response = await api.post('/inscription/creer-complete/', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.status === 'success') {
        console.log('✅ Inscription créée avec succès');
        toast.success("Inscription créée avec succès");
        navigate('/inscriptions');
      } else {
        throw new Error(response.data.message || "Erreur lors de la création de l'inscription");
      }
    } catch (error) {
      console.error('❌ Erreur lors de la création de l\'inscription:', error);
      const errorMessage = error.response?.data?.message || error.message || "Une erreur est survenue";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [
    formData,
    documents,
    tarifsSup,
    validateStep,
    setLoading,
    setError,
    genererLignesVersement,
    genererModalitesPaiement,
    navigate
  ]);

  return {
    handleSubmit,
    genererLignesVersement,
    genererModalitesPaiement
  };
};

export default useInscriptionSubmit; 